import { useMutation } from '@tanstack/react-query';
import { Blockquote, Button, Card, Label, Select, Table, TextInput } from 'flowbite-react';
import { useCallback, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';

import type {
  FlowMessage,
  FlowMessageType,
  SPOptimizerMessage,
  SPOptimizerMessageExperiment,
  SPOptimizerStore,
  Store,
} from 'src/api/models';
import { mutations } from 'src/api/mutations';
import { sleep } from 'src/utils';

interface Flow {
  id: number;
  flow_id: string;
  name: string;
}

interface Context {
  store: Store;
  storeAdminUrl: string;
  flow: Flow;
  flowAdminUrl: string;
  flowMessage: FlowMessage;
  flowMessageAdminUrl: string;
  spOptimizerStore: SPOptimizerStore | null;
  spOptimizerMessage: SPOptimizerMessage | null;
  spOptimizerMessageExperiment: SPOptimizerMessageExperiment | null;
}

function TitleWithLink({
  title,
  object,
  link,
}: {
  title: string;
  object: null | { id: number };
  link: null | string;
}) {
  return (
    <p className='font-semibold'>
      {title}
      {Boolean(object) && object && (
        <>
          :{' '}
          <Link
            target='blank'
            to={link ?? ''}
            reloadDocument
            className='font-medium text-blue-600 underline hover:no-underline'
          >
            {object.id}
          </Link>
        </>
      )}
    </p>
  );
}

function waitForIdle(): Promise<void> {
  return new Promise((resolve) => {
    requestIdleCallback(() => {
      resolve();
    });
  });
}

async function drawToCanvas(node: HTMLElement, track: MediaStreamTrack): Promise<string> {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('failed to create canvas2d context');
  }

  document.head.insertAdjacentHTML(
    'beforeend',
    '<style id="disable-cursor">#video {cursor:none} video::-webkit-media-controls,audio::-webkit-media-controls {display:none !important;}</style>',
  );

  document.body.scrollTo({ top: 0, behavior: 'instant' });
  await sleep(1500);
  await waitForIdle();

  const boundingRect = node.getBoundingClientRect();
  const nodeWidth = boundingRect.width;
  const nodeHeight = boundingRect.height;
  canvas.width = nodeWidth;
  canvas.height = nodeHeight;

  const { width: videoWidth, height: videoHeight } = track.getSettings();
  if (!videoWidth || !videoHeight) {
    throw new Error('failed to get video stream dimensions');
  }

  // @ts-expect-error: TS doesn't know about MediaStreamTrackProcessor yet
  const mediaProcessor = new MediaStreamTrackProcessor({ track, maxBufferSize: 1 });
  const reader = mediaProcessor.readable.getReader();

  const { clientWidth: screenWidth, clientHeight: screenHeight } = document.documentElement;

  console.log('video=', videoWidth, videoHeight);
  console.log('iframe=', nodeWidth, nodeHeight);
  console.log('window=', screenWidth, screenHeight);

  let dy = 0;
  let dh = screenHeight;

  await waitForIdle();
  while (dy < nodeHeight) {
    const { width: videoWidth, height: videoHeight } = track.getSettings();
    console.log('painting', dy, dh, videoHeight);
    const result = await reader.read();
    const frame = result.value;
    context.drawImage(frame, 0, 0, videoWidth!, videoHeight!, 0, dy, nodeWidth, dh);
    frame.close();
    dy += screenHeight - 8; // Don't know why I need the magic 2 here!
    node.style.top = `-${dy}px`;
    dh = Math.min(screenHeight, nodeHeight - dy);
    await sleep(1500);
    await waitForIdle();
  }

  await reader.cancel();
  node.style.top = '0px';
  document.getElementById('disable-cursor')?.remove();

  return canvas.toDataURL('image/png');
}

async function captureNode(node: HTMLElement): Promise<string> {
  const captureStream = await navigator.mediaDevices.getDisplayMedia({
    audio: false,
    video: {
      displaySurface: 'browser',
      frameRate: { ideal: 10, max: 30 },
    },
    // @ts-expect-error: ts doesn't know about some of these properties yet
    preferCurrentTab: true,
    surfaceSwitching: 'exclude',
    monitorTypeSurfaces: 'exclude',
  });

  const [track] = captureStream.getVideoTracks();
  if (!track) {
    throw new Error('failed to get video track from capture stream');
  }
  track.contentHint = 'text';
  // @ts-expect-error: ts doesn't know about CropTarget yet
  const cropTarget = await CropTarget.fromElement(node);
  // @ts-expect-error: ts doesn't know about cropTo yet
  await track.cropTo(cropTarget);
  const frame = await drawToCanvas(node, track);
  captureStream.getTracks().forEach((track) => track.stop());
  return frame;
}

export function AdminCreateSPOptimizerExperiment() {
  const outletContext = useOutletContext() as Context;
  const { store, storeAdminUrl, flow, flowAdminUrl, flowMessage, flowMessageAdminUrl } =
    useOutletContext() as Context;

  const emailIframe = useRef<HTMLIFrameElement>(null);
  const [processing, setProcessing] = useState(false);
  const [saveMessages, setSaveMessages] = useState<string[]>([]);
  const [spOptimizerStore, setSpOptimizerStore] = useState(outletContext.spOptimizerStore);
  const [spOptimizerMessage, setSpOptimizerMessage] = useState(outletContext.spOptimizerMessage);
  const [spOptimizerMessageExperiment, setSpOptimizerMessageExperiment] = useState(
    outletContext.spOptimizerMessageExperiment,
  );

  const createSPOptimizerStoreFromFlowMessage = useMutation(
    mutations.createSPOptimizerStoreFromFlowMessage(),
  );

  const createSPOptimizerMessageFromFlowMessage = useMutation(
    mutations.createSPOptimizerMessageFromFlowMessage(),
  );

  const createSPOptimizerMessageExperimentFromFlowMessage = useMutation(
    mutations.createSPOptimizerMessageExperimentFromFlowMessage(),
  );

  const renderEmailTemplate = useMutation(mutations.renderEmailTemplate());

  const createNew = useCallback(async () => {
    setSaveMessages([]);
    if (emailIframe.current === null) {
      setSaveMessages((m) => [...m, 'No iframe present']);
      return;
    }
    setProcessing(true);
    let localSpOptimizerStore = spOptimizerStore;
    let localSpOptimizerMessage = spOptimizerMessage;
    try {
      if (localSpOptimizerStore === null) {
        setSaveMessages((m) => [...m, 'Creating SP Optimizer Store...']);
        const aboutPageUrl = (document.getElementById('about-page-url') as HTMLInputElement).value;
        localSpOptimizerStore = await createSPOptimizerStoreFromFlowMessage.mutateAsync([
          flowMessage.id,
          aboutPageUrl,
        ]);
        if (localSpOptimizerStore === null) {
          throw new Error('failed to create sp_optimizer_store, api returned null');
        }
        setSpOptimizerStore(localSpOptimizerStore);
        setSaveMessages((m) => [...m, 'Created SP Optimizer Store']);
      }
      if (localSpOptimizerMessage === null) {
        setSaveMessages((m) => [...m, 'Creating SP Optimizer Message...']);
        const flowMessageType = (document.getElementById('flow-message-type') as HTMLSelectElement)
          .value as FlowMessageType;
        const emailPreviewLink = (document.getElementById('email-preview-link') as HTMLInputElement)
          .value;
        let imageDataUrl: string | undefined = undefined;
        if (!!emailPreviewLink || emailPreviewLink === '') {
          setSaveMessages((m) => [...m, 'Rendering flow message template...']);
          const emailTemplateRender = await renderEmailTemplate.mutateAsync([flowMessage.id]);
          setSaveMessages((m) => [...m, 'Converting template html to image...']);
          emailIframe.current.style.display = 'block';
          emailIframe.current.srcdoc = emailTemplateRender.html;
          await sleep(1000);
          emailIframe.current.style.height = `${emailIframe.current.contentDocument?.body.scrollHeight ?? 0}px`;
          await sleep(1000);
          imageDataUrl = await captureNode(emailIframe.current);
          setSaveMessages((m) => [...m, 'Template preview image generated!']);
          const tab = window.open('about:blank');
          const image = new Image();
          image.src = imageDataUrl;
          setTimeout(function () {
            const body = tab!.document.getElementsByTagName('body')[0];
            if (body) {
              body.innerHTML = image.outerHTML;
            }
          }, 100);

          emailIframe.current.srcdoc = '';
          emailIframe.current.style.height = '0px';
          emailIframe.current.style.display = 'none';
        }
        localSpOptimizerMessage = await createSPOptimizerMessageFromFlowMessage.mutateAsync([
          flowMessage.id,
          localSpOptimizerStore.id,
          flowMessageType,
          emailPreviewLink,
          imageDataUrl,
        ]);
        setSpOptimizerMessage(localSpOptimizerMessage);
        setSaveMessages((m) => [...m, 'Created SP Optimizer Message']);
      }
      setSaveMessages((m) => [...m, 'Creating SP Optimizer Experiment...']);
      const spOptimizerMessageExperiment =
        await createSPOptimizerMessageExperimentFromFlowMessage.mutateAsync([
          flowMessage.id,
          localSpOptimizerStore.id,
          localSpOptimizerMessage.id,
        ]);
      setSpOptimizerMessageExperiment(spOptimizerMessageExperiment);
      setSaveMessages((m) => [...m, 'Created SP Optimizer Experiment']);
    } catch (e) {
      console.log(e);
      if (e instanceof Error) {
        const error = e;
        setSaveMessages((m) => [...m, `Error: ${error.message}`]);
      } else if (e && typeof e === 'object' && 'error' in e) {
        const obj = e;
        setSaveMessages((m) => [...m, `Error: ${obj.error}`]);
      } else {
        setSaveMessages((m) => [...m, `Error: ${e}`]);
      }
    } finally {
      setProcessing(false);
    }
  }, [
    createSPOptimizerMessageExperimentFromFlowMessage,
    createSPOptimizerMessageFromFlowMessage,
    createSPOptimizerStoreFromFlowMessage,
    flowMessage.id,
    renderEmailTemplate,
    spOptimizerMessage,
    spOptimizerStore,
  ]);

  return (
    <>
      <iframe
        className='absolute left-0 right-0 top-0 isolate z-10 mx-auto hidden w-[800px] overflow-visible overflow-y-scroll'
        sandbox='allow-same-origin'
        src='about:blank'
        ref={emailIframe}
      />
      <div className='container mx-auto w-1/2 pt-10'>
        <Card className='w-full'>
          <p className='text-xl	font-semibold'>Create New SP Optimizer Experiment</p>
          <div className='flex w-full flex-col gap-6'>
            <div className='flex flex-col gap-2'>
              <p className='mr-4 truncate text-sm'>
                <span className='font-semibold'>Store: </span>
                <Link
                  target='blank'
                  to={storeAdminUrl}
                  reloadDocument
                  className='font-medium text-blue-600 underline hover:no-underline'
                >
                  {store.key_name}
                </Link>
              </p>
              <p className='mr-4 truncate text-sm '>
                <span className='font-semibold'>Flow: </span>
                <Link
                  target='blank'
                  to={flowAdminUrl}
                  reloadDocument
                  className='font-medium text-blue-600 underline hover:no-underline'
                >
                  {flow.flow_id} | {flow.name}
                </Link>
              </p>
              <p className='mr-4 truncate text-sm '>
                <span className='font-semibold'>Message: </span>
                <Link
                  target='blank'
                  to={flowMessageAdminUrl}
                  reloadDocument
                  className='font-medium text-blue-600 underline hover:no-underline'
                >
                  {flowMessage.flow_message_id} | {flowMessage.name}
                </Link>
              </p>
            </div>

            <div className='flex flex-col gap-2'>
              <TitleWithLink
                title='Optimizer Store'
                object={spOptimizerStore}
                link={spOptimizerStore?.admin_url ?? ''}
              ></TitleWithLink>
              {Boolean(spOptimizerStore) && spOptimizerStore !== null && (
                <>
                  {Boolean(spOptimizerStore.about_page_url) &&
                    spOptimizerStore.about_page_url !== null && (
                      <p className='mr-4 truncate text-sm'>
                        <span className='font-medium'>
                          Store about page:{' '}
                          <span className='font-normal'>
                            <Link
                              target='blank'
                              to={spOptimizerStore.about_page_url ?? ''}
                              reloadDocument
                              className='font-medium text-blue-600 underline hover:no-underline'
                            >
                              {spOptimizerStore.about_page_url ?? '---Not Saved---'}
                            </Link>
                          </span>
                        </span>
                      </p>
                    )}
                  <div className='mr-4 text-sm'>
                    <span className='font-medium'>Store Description</span>
                    <Blockquote className='my-4 max-h-24 overflow-y-scroll whitespace-pre-wrap border-l-4 border-gray-300 bg-gray-50 p-4 text-xs font-normal'>
                      {spOptimizerStore.store_prompt}
                    </Blockquote>
                  </div>
                </>
              )}
              {Boolean(spOptimizerStore) || (
                <div className='max-w-md'>
                  <div className='mb-2 block'>
                    <Label htmlFor='about-page-url' value='Store about page URL' />
                  </div>
                  <TextInput
                    id='about-page-url'
                    type='url'
                    placeholder='https://manlybands.com/pages/about'
                    helperText='Leave blank and GPT will guess'
                  />
                </div>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <TitleWithLink
                title='Optimizer Message'
                object={spOptimizerMessage}
                link={spOptimizerMessage?.admin_url ?? ''}
              ></TitleWithLink>
              {Boolean(spOptimizerMessage) && spOptimizerMessage !== null && (
                <>
                  <p className='mr-4 text-sm'>
                    <span className='font-medium'>Type: </span>
                    <span>{spOptimizerMessage.flow_message_type}</span>
                  </p>
                  <p className='mr-4 truncate text-sm'>
                    <span className='font-medium'>Email Preview Link: </span>
                    {spOptimizerMessage.email_preview_link !== undefined && (
                      <Link
                        target='blank'
                        to={spOptimizerMessage.email_preview_link || ''}
                        reloadDocument
                        className='font-medium text-blue-600 underline hover:no-underline'
                      >
                        {spOptimizerMessage.email_preview_link.split('/').slice(-1)}
                      </Link>
                    )}
                  </p>
                  <div className='mr-4 text-sm'>
                    <span className='font-medium'>Email Description:</span>
                    <Blockquote className='my-4 max-h-24 overflow-y-scroll whitespace-pre-wrap border-l-4 border-gray-300 bg-gray-50 p-4 text-xs font-normal'>
                      {spOptimizerMessage.email_prompt}
                    </Blockquote>
                  </div>
                </>
              )}

              {Boolean(spOptimizerMessage) || (
                <>
                  <div className='max-w-lg'>
                    <div className='mr-4 inline'>
                      <Label htmlFor='flow-message-type' value='Type:' />
                    </div>
                    <Select id='flow-message-type' required className='inline-flex w-auto'>
                      <option value='not-selected'>--- Choose ---</option>
                      <option value='WELCOME'>Welcome</option>
                      <option value='ABANDONEDCART'>Abandoned Cart</option>
                      <option value='THANKYOU'>Thank You</option>
                    </Select>
                  </div>

                  {/* <fieldset className='flex max-w-md flex-col gap-4'>
                  <legend className='mb-4'>Type:</legend>
                  <div className='flex items-center gap-2'>
                    <Radio id='welcome' name='flow-message-type' value='WELCOME' />
                    <Label htmlFor='welcome'>Welcome</Label>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Radio id='abandonedcart' name='flow-message-type' value='ABANDONEDCART' />
                    <Label htmlFor='abandonedcart'>Abandoned Cart</Label>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Radio id='thankyou' name='flow-message-type' value='THANKYOU' />
                    <Label htmlFor='thankyou'>Thank You</Label>
                  </div>
                </fieldset> */}
                  <div className='max-w-xl'>
                    <div className='mb-2 block'>
                      <Label htmlFor='email-preview-link' value='Link to email preview image' />
                    </div>
                    <TextInput
                      id='email-preview-link'
                      type='url'
                      placeholder='https://storage.googleapis.com/maverick_email_previews/preview.png'
                      helperText='Leave blank and we will render the template and upload to the bucket'
                    />
                  </div>
                </>
              )}
            </div>

            {Boolean(spOptimizerMessageExperiment) && spOptimizerMessageExperiment !== null && (
              <div className='flex flex-col gap-2'>
                <TitleWithLink
                  title='Newest Experiment'
                  object={spOptimizerMessageExperiment}
                  link={spOptimizerMessageExperiment.admin_url ?? ''}
                ></TitleWithLink>
                <p className='mr-4 text-sm'>
                  <span className='font-medium'>Phase: </span>
                  {spOptimizerMessageExperiment.prompt_version}/
                  {spOptimizerMessageExperiment.experiment_phase}
                </p>
                <Table striped>
                  <Table.Head>
                    <Table.HeadCell>Shard</Table.HeadCell>
                    <Table.HeadCell>Subject</Table.HeadCell>
                    <Table.HeadCell>Preview</Table.HeadCell>
                  </Table.Head>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell>Control</Table.Cell>
                      <Table.Cell>{spOptimizerMessageExperiment.default_subject}</Table.Cell>
                      <Table.Cell>{spOptimizerMessageExperiment.default_preview}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>1</Table.Cell>
                      <Table.Cell>{spOptimizerMessageExperiment.subject_1}</Table.Cell>
                      <Table.Cell>{spOptimizerMessageExperiment.preview_1}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>2</Table.Cell>
                      <Table.Cell>{spOptimizerMessageExperiment.subject_2}</Table.Cell>
                      <Table.Cell>{spOptimizerMessageExperiment.preview_2}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>3</Table.Cell>
                      <Table.Cell>{spOptimizerMessageExperiment.subject_3}</Table.Cell>
                      <Table.Cell>{spOptimizerMessageExperiment.preview_3}</Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
                <p className='mr-4 font-mono text-xs'>
                  <span className='font-medium'>Subject Template: </span>
                  <span className='bg-slate-100 p-1'>
                    {spOptimizerMessageExperiment.klaviyo_subject_template}
                  </span>
                </p>
                <p className='mr-4 font-mono text-xs'>
                  <span className='font-medium'>Preview Template: </span>
                  <span className='bg-slate-100 p-1'>
                    {spOptimizerMessageExperiment.klaviyo_preview_template}
                  </span>
                </p>
              </div>
            )}

            <Button
              className='w-48'
              color='red'
              onClick={createNew}
              isProcessing={processing}
              disabled={processing}
            >
              Create {spOptimizerMessageExperiment !== null && 'New'}
            </Button>

            <Blockquote className='my-4 overflow-y-scroll whitespace-pre-wrap border-l-4 border-gray-300 bg-gray-50 p-4 text-xs font-normal'>
              {saveMessages.map((m, i) => (
                <p key={i} className='font-mono'>
                  {m}
                </p>
              ))}
            </Blockquote>
          </div>
        </Card>
      </div>
    </>
  );
}
